import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, User, LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

export default function PortalPicker() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  const hasErp = !!user?.permissions?.includes("erp.access");
  const isJamaah = !!user?.roles?.includes("jamaah");
  const isUC = !!user?.roles?.includes("umrah_consultant");
  // UC bisa akses Portal Jamaah (untuk booking/dokumen pribadi mereka)
  const hasJamaahPortal = isJamaah || isUC;
  const hasBoth = hasErp && hasJamaahPortal;

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    // Kalau hanya punya satu akses, langsung redirect
    if (hasErp && !hasJamaahPortal) nav("/erp", { replace: true });
    else if (!hasErp && hasJamaahPortal) nav("/portal", { replace: true });
  }, [user, loading, hasErp, hasJamaahPortal, nav]);

  if (!user || (hasErp && !hasJamaahPortal) || (!hasErp && hasJamaahPortal)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FBF9F4] text-sm text-[#4A4844]">
        Mengalihkan...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F4] px-6 py-16">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Royal Saudi Haramain" className="h-10 w-auto" />
          </Link>
          <button onClick={logout} data-testid="portal-picker-logout"
            className="flex items-center gap-2 rounded-xl border border-[#141414] px-4 py-2 text-sm font-semibold text-[#141414]">
            <LogOut size={14} /> Keluar
          </button>
        </div>

        <div className="mt-14 text-center">
          <div className="eyebrow">Selamat datang</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-[#141414] sm:text-4xl">
            {user.full_name}
          </h1>
          <p className="mt-3 text-sm text-[#4A4844]">
            Pilih portal yang ingin Anda buka.
          </p>
        </div>

        <div className={`mt-10 grid gap-6 ${hasBoth ? "md:grid-cols-2" : "md:grid-cols-1 max-w-md mx-auto"}`}>
          {hasJamaahPortal && (
            <Link to="/portal" data-testid="picker-portal-jamaah"
              className="group rounded-2xl border border-[#E5DEC9] bg-white p-8 transition-all hover:border-[#C5A028] hover:shadow-lg">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F4EFE6] text-[#C5A028] group-hover:bg-[#C5A028] group-hover:text-white">
                <User size={26} />
              </div>
              <h2 className="mt-5 font-display text-xl font-bold text-[#141414]">Portal Jamaah</h2>
              <p className="mt-2 text-sm text-[#4A4844]">
                Kelola booking, dokumen, tagihan, dan progres keberangkatan Anda.
              </p>
              <div className="mt-4 text-sm font-bold text-[#C5A028] group-hover:underline">
                Buka Portal Jamaah →
              </div>
            </Link>
          )}
          {hasErp && (
            <Link to="/erp" data-testid="picker-portal-erp"
              className="group rounded-2xl border border-[#E5DEC9] bg-[#0A0A0A] p-8 text-white transition-all hover:border-[#C5A028] hover:shadow-lg">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1F1F1F] text-[#D4AF37] group-hover:bg-[#C5A028] group-hover:text-black">
                <Briefcase size={26} />
              </div>
              <h2 className="mt-5 font-display text-xl font-bold">Portal ERP</h2>
              <p className="mt-2 text-sm text-[#C7C0B2]">
                Dashboard operasional untuk staff, mitra, finance, dan admin.
              </p>
              <div className="mt-4 text-sm font-bold text-[#D4AF37] group-hover:underline">
                Buka Portal ERP →
              </div>
            </Link>
          )}
        </div>

        <div className="mt-10 text-center text-xs text-[#8A867C]">
          Peran Anda: <b>{user.role_labels?.join(", ")}</b>
        </div>
      </div>
    </div>
  );
}
